import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';

export interface ObjectParametersDialogData {
    object: any;
    parameters: { [key: string]: any };
    headers: string[];
    rowData: any[];
}

@Component({
    selector: 'app-object-parameters-dialog',
    templateUrl: './object-parameters-dialog.component.html',
    styleUrls: ['./object-parameters-dialog.component.scss']
})
export class ObjectParametersDialogComponent implements OnInit {
    parametersForm: FormGroup;
    parameterKeys: string[] = [];
    originalValues: { [key: string]: any } = {};

    constructor(
        public dialogRef: MatDialogRef<ObjectParametersDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ObjectParametersDialogData,
        private fb: FormBuilder
    ) {
        this.parametersForm = this.fb.group({});
    }

    ngOnInit(): void {
        this.setupForm();
    }

    private setupForm(): void {
        if (!this.data.parameters) return;

        // Get parameter keys from headers, excluding first two columns (name and type)
        this.parameterKeys = this.data.headers.slice(2);

        // Create form controls for each parameter
        const formControls: { [key: string]: FormControl } = {};

        this.parameterKeys.forEach((key, index) => {
            const value = this.data.rowData[index + 2] || '';
            this.originalValues[key] = value;
            formControls[key] = new FormControl(value);
        });

        this.parametersForm = this.fb.group(formControls);
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onApply(): void {
        const formValues = this.parametersForm.value;
        const changes: { [key: string]: any } = {};

        // Only include changed values
        this.parameterKeys.forEach(key => {
            if (formValues[key] !== this.originalValues[key]) {
                changes[key] = formValues[key];
            }
        });

        this.dialogRef.close({
            changes,
            allValues: formValues
        });
    }

    isFormValid(): boolean {
        return this.parametersForm.valid;
    }

    hasChanges(): boolean {
        const formValues = this.parametersForm.value;
        return this.parameterKeys.some(key => formValues[key] !== this.originalValues[key]);
    }

    resetForm(): void {
        this.parametersForm.reset(this.originalValues);
    }

    getParameterDisplayName(key: string): string {
        // Convert camelCase or snake_case to display name
        return key.replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .replace(/^./, str => str.toUpperCase());
    }

    isNumericParameter(key: string): boolean {
        // Check if the parameter should be treated as numeric
        const numericParams = ['x', 'y', 'z', 'rx', 'ry', 'rz', 'sx', 'sy', 'sz',
            'width', 'height', 'depth', 'length', 'radius', 'size',
            'scale', 'rotation', 'position'];
        return numericParams.some(param => key.toLowerCase().includes(param));
    }

    isColorParameter(key: string): boolean {
        // Check if the parameter should be treated as color
        const colorParams = ['color', 'material'];
        return colorParams.some(param => key.toLowerCase().includes(param));
    }
} 